import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toTitleCase } from "@/lib/utils";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "http://localhost:8000").replace(/\/$/, "");
const API = `${BACKEND_URL}/api`;

const emptyFilters = {
  name: "",
  cedula: "",
  level: "",
  branch: "",
  title: "",
  company: "",
  position: "",
};

const levelLabels = {
  tecnica: "Técnica",
  tecnologica: "Tecnológica",
  universitaria: "Universitaria",
  especializacion: "Especialización",
  maestria: "Maestría",
  doctorado: "Doctorado",
  curso: "Curso / Certificación",
};

const formatLevelLabel = (value) => levelLabels[value] || toTitleCase(value || "");

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("admin_token") || "");
  const [loginForm, setLoginForm] = useState({ username: "", password: "" });
  const [filters, setFilters] = useState(emptyFilters);
  const [options, setOptions] = useState({
    levels: [],
    branches: [],
    titles: [],
    companies: [],
    positions: [],
  });
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);

  const authHeaders = useMemo(() => {
    if (!token) return {};
    return { Authorization: `Bearer ${token}` };
  }, [token]);

  const loadFilters = async () => {
    try {
      const response = await axios.get(`${API}/admin/filters`, {
        headers: authHeaders,
      });
      setOptions(response.data);
    } catch (error) {
      console.error("Error loading admin filters:", error);
      if (error?.response?.status === 401) {
        handleLogout();
      } else {
        toast.error("No Se Pudieron Cargar Los Filtros");
      }
    }
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    if (!loginForm.username || !loginForm.password) {
      toast.error("Ingrese Usuario Y Contraseña");
      return;
    }
    setAuthLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, loginForm);
      const receivedToken = response.data.token;
      setToken(receivedToken);
      localStorage.setItem("admin_token", receivedToken);
      toast.success("Sesión Iniciada");
    } catch (error) {
      console.error("Error during admin login:", error);
      toast.error("Credenciales Inválidas");
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    setToken("");
    setResults([]);
    setOptions({
      levels: [],
      branches: [],
      titles: [],
      companies: [],
      positions: [],
    });
  };

  const handleSearch = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params[key] = value;
      });
      const response = await axios.get(`${API}/admin/search`, {
        headers: authHeaders,
        params,
      });
      setResults(response.data.results || []);
    } catch (error) {
      console.error("Error searching candidates:", error);
      if (error?.response?.status === 401) {
        handleLogout();
      } else {
        toast.error("No Se Pudieron Cargar Los Resultados");
      }
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = () => {
    setFilters(emptyFilters);
    setResults([]);
  };

  useEffect(() => {
    if (token) {
      loadFilters();
    }
  }, [token]);

  if (!token) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <Card className="w-full max-w-md border border-gray-200 rounded-sm">
          <CardContent className="p-6">
            <h1 className="font-oswald text-2xl font-bold text-black tracking-wide mb-2">
              Acceso Administrador
            </h1>
            <p className="text-sm text-gray-500 mb-6">
              Ingrese Sus Credenciales Para Continuar.
            </p>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label className="text-sm font-medium text-gray-700 tracking-wide">
                  Usuario
                </Label>
                <Input
                  value={loginForm.username}
                  onChange={(event) =>
                    setLoginForm((prev) => ({ ...prev, username: event.target.value }))
                  }
                  placeholder="Admin"
                  className="mt-1 h-12 rounded-sm"
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-700 tracking-wide">
                  Contraseña
                </Label>
                <Input
                  type="password"
                  value={loginForm.password}
                  onChange={(event) =>
                    setLoginForm((prev) => ({ ...prev, password: event.target.value }))
                  }
                  placeholder="********"
                  className="mt-1 h-12 rounded-sm"
                />
              </div>
              <Button
                type="submit"
                disabled={authLoading}
                className="w-full bg-black text-yellow-400 hover:bg-gray-900 h-12 rounded-sm tracking-wide font-bold"
              >
                {authLoading ? "Ingresando..." : "Ingresar"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6 lg:p-10">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="font-oswald text-3xl font-bold text-black tracking-wide">
              Panel De Búsqueda
            </h1>
            <p className="text-sm text-gray-500">
              Filtre Candidatos Por Datos Personales, Estudios Y Experiencia.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={handleLogout}
            className="h-10 rounded-sm tracking-wide"
          >
            Cerrar Sesión
          </Button>
        </div>

        <Card className="border border-gray-200 rounded-sm">
          <CardContent className="p-6 space-y-4">
            <h2 className="font-oswald text-lg font-semibold">Filtros</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <Label className="text-sm font-medium text-gray-700 tracking-wide">
                  Nombre
                </Label>
                <Input
                  value={filters.name}
                  onChange={(event) => setFilters((prev) => ({ ...prev, name: event.target.value }))}
                  placeholder="Nombre O Apellido"
                  className="mt-1 h-12 rounded-sm"
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-700 tracking-wide">
                  Cédula
                </Label>
                <Input
                  value={filters.cedula}
                  onChange={(event) => setFilters((prev) => ({ ...prev, cedula: event.target.value }))}
                  placeholder="Número De Cédula"
                  className="mt-1 h-12 rounded-sm"
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-700 tracking-wide">
                  Nivel Académico
                </Label>
                <Select
                  value={filters.level || undefined}
                  onValueChange={(value) => setFilters((prev) => ({ ...prev, level: value }))}
                >
                  <SelectTrigger className="mt-1 h-12 rounded-sm">
                    <SelectValue placeholder="Seleccione Un Nivel" />
                  </SelectTrigger>
                  <SelectContent>
                    {options.levels.map((item) => (
                      <SelectItem key={item} value={item}>
                        {formatLevelLabel(item)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-700 tracking-wide">
                  Rama
                </Label>
                <Select
                  value={filters.branch || undefined}
                  onValueChange={(value) => setFilters((prev) => ({ ...prev, branch: value }))}
                >
                  <SelectTrigger className="mt-1 h-12 rounded-sm">
                    <SelectValue placeholder="Seleccione Una Rama" />
                  </SelectTrigger>
                  <SelectContent>
                    {options.branches.map((item) => (
                      <SelectItem key={item} value={item}>
                        {item}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-700 tracking-wide">
                  Título
                </Label>
                <Select
                  value={filters.title || undefined}
                  onValueChange={(value) => setFilters((prev) => ({ ...prev, title: value }))}
                >
                  <SelectTrigger className="mt-1 h-12 rounded-sm">
                    <SelectValue placeholder="Seleccione Un Título" />
                  </SelectTrigger>
                  <SelectContent>
                    {options.titles.map((item) => (
                      <SelectItem key={item} value={item}>
                        {item}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-700 tracking-wide">
                  Empresa
                </Label>
                <Select
                  value={filters.company || undefined}
                  onValueChange={(value) => setFilters((prev) => ({ ...prev, company: value }))}
                >
                  <SelectTrigger className="mt-1 h-12 rounded-sm">
                    <SelectValue placeholder="Seleccione Una Empresa" />
                  </SelectTrigger>
                  <SelectContent>
                    {options.companies.map((item) => (
                      <SelectItem key={item} value={item}>
                        {item}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-700 tracking-wide">
                  Cargo
                </Label>
                <Select
                  value={filters.position || undefined}
                  onValueChange={(value) => setFilters((prev) => ({ ...prev, position: value }))}
                >
                  <SelectTrigger className="mt-1 h-12 rounded-sm">
                    <SelectValue placeholder="Seleccione Un Cargo" />
                  </SelectTrigger>
                  <SelectContent>
                    {options.positions.map((item) => (
                      <SelectItem key={item} value={item}>
                        {item}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                type="button"
                onClick={handleSearch}
                disabled={loading}
                className="bg-black text-yellow-400 hover:bg-gray-900 h-11 rounded-sm tracking-wide font-bold"
              >
                {loading ? "Buscando..." : "Buscar"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={resetFilters}
                className="h-11 rounded-sm tracking-wide"
              >
                Limpiar Filtros
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-gray-200 rounded-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-oswald text-lg font-semibold">Resultados</h2>
              <span className="text-sm text-gray-500">
                {results.length} Registros
              </span>
            </div>

            {results.length === 0 ? (
              <p className="text-sm text-gray-500">No Hay Resultados Para Mostrar.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Cédula</TableHead>
                    <TableHead>Contacto</TableHead>
                    <TableHead>Estudios</TableHead>
                    <TableHead>Experiencia</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {results.map((profile) => {
                    const personal = profile.personal_data || {};
                    const fullName = `${personal.first_name || ""} ${personal.last_name || ""}`.trim() || "Sin Nombre";
                    return (
                      <TableRow key={profile.id}>
                        <TableCell>
                          <div className="font-semibold text-gray-900">{fullName}</div>
                          <div className="text-xs text-gray-400">{profile.id}</div>
                        </TableCell>
                        <TableCell className="text-sm text-gray-600">
                          {personal.cedula || "-"}
                        </TableCell>
                        <TableCell className="text-sm text-gray-600">
                          <div>{personal.email || "-"}</div>
                          <div>{personal.phone || "-"}</div>
                        </TableCell>
                        <TableCell className="text-sm text-gray-600">
                          {profile.academic_info?.length ? (
                            <div className="space-y-1">
                              {profile.academic_info.map((entry) => (
                                <div key={entry.id} className="text-xs">
                                  {entry.title}
                                  {entry.level ? ` (${formatLevelLabel(entry.level)})` : ""}
                                </div>
                              ))}
                            </div>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-gray-600">
                          {profile.work_experience?.length ? (
                            <div className="space-y-1">
                              {profile.work_experience.map((entry) => (
                                <div key={entry.id} className="text-xs">
                                  {entry.position} - {entry.company}
                                </div>
                              ))}
                            </div>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
